/**
 * useFinancialProfile Hook
 * Custom hook for financial profile management
 */

import { useEffect } from 'react';
import { useProfileStore } from '../store/profileStore';
import { aiApi } from '../api/endpoints/ai';
import type { FinancialProfile } from '../api/types';
import logger from '../utils/logger';

export function useFinancialProfile() {
  const {
    profile,
    hasProfile,
    isLoading,
    error,
    setProfile,
    updateProfile,
    setHasProfile,
    setLoading,
    setError,
  } = useProfileStore();

  // Load profile on mount
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await aiApi.getFinancialProfile();
      if (response.has_profile && response.financial_profile) {
        setProfile(response.financial_profile);
        setHasProfile(true);
      } else {
        setHasProfile(false);
      }
    } catch (err: any) {
      logger.error('Error loading profile:', err);
      setError('Erreur lors du chargement du profil financier');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async (profileData: Partial<FinancialProfile>) => {
    setLoading(true);
    setError(null);

    try {
      const response = await aiApi.updateFinancialProfile(profileData);
      setProfile(response.financial_profile);
      setHasProfile(true);
      return { success: true };
    } catch (err: any) {
      logger.error('Error saving profile:', err);
      
      if (err.response?.data?.errors) {
        const apiErrors = err.response.data.errors;
        const firstError = Object.values(apiErrors)[0];
        const errorMessage = Array.isArray(firstError) ? firstError[0] : String(firstError);
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } else {
        const errorMessage = 'Erreur lors de la sauvegarde du profil';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    profile,
    hasProfile,
    isLoading,
    error,
    loadProfile,
    saveProfile,
    updateProfile,
  };
}

